import { useEffect } from 'react';
import { getHomeworkById, getHomework } from '../utils/homeworkService';
import { getStudentByInviteSlug } from '../utils/studentsService';

// Загружаем содержимое homework контейнеров при открытии homework из URL
export function useHomeworkContentLoader({
  homeworkId, searchParams, isStudent, lessonId, studentId, currentUser,
  setWhiteboards, setHomeworkContainers, setEditorContents, setContainerComments,
  setHomeworkDataCache, setSubmittedExerciseStates,
}) {
  useEffect(() => {
    const currentHomeworkId = searchParams.get('homeworkId') || homeworkId;

    // Загружаем homework контейнеры для студентов И для преподавателей (когда они открывают homework для проверки)
    if (currentHomeworkId) {
      const loadHomeworkData = async () => {
        try {
          const hwId = String(currentHomeworkId);

          // Проверяем, является ли ID UUID
          const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(hwId);

          let supabaseHomework;
          if (isUUID) {
            // Если это UUID, загружаем напрямую
            supabaseHomework = await getHomeworkById(hwId);
          } else {
            // Если это timestamp, ищем homework по lessonId и studentId
            console.log('Homework ID is timestamp, searching by lessonId and studentId');

            // Конвертируем studentId slug в UUID для Supabase
            let studentUuidForQuery = studentId || 'demo-student-1';
            const isStudentIdUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(studentUuidForQuery));
            if (!isStudentIdUUID && studentUuidForQuery !== 'demo-student-1') {
              try {
                const studentData = await getStudentByInviteSlug(studentUuidForQuery);
                if (studentData && studentData.id) {
                  studentUuidForQuery = studentData.id;
                  console.log('Converted student slug to UUID:', studentId, '->', studentUuidForQuery);
                }
              } catch (e) {
                console.warn('Could not convert student slug to UUID:', e);
              }
            }

            const allHomework = await getHomework(studentUuidForQuery);
            // Ищем homework, который связан с текущим уроком
            supabaseHomework = allHomework.find(hw => {
              return hw.lessonId === lessonId || hw.lessonId === String(lessonId);
            }) || null;
          }

          if (!supabaseHomework || !supabaseHomework.id) {
            console.warn('Homework not found in Supabase:', hwId, 'isUUID:', isUUID);
            return;
          }

          console.log('Loaded homework from Supabase:', supabaseHomework);
          console.log('Homework title:', supabaseHomework.title);
          console.log('Homework description:', supabaseHomework.description);
          console.log('Homework dueDate:', supabaseHomework.dueDate);

          // Обновляем кеш с данными из Supabase (используем данные напрямую из convertHomeworkFromSupabaseFormat)
          const homeworkData = {
            id: supabaseHomework.id,
            title: supabaseHomework.title || '',
            description: supabaseHomework.description || '',
            dueDate: supabaseHomework.dueDate || null,
            status: supabaseHomework.status || 'assigned',
            studentId: supabaseHomework.studentId,
            lessonId: supabaseHomework.lessonId,
            containerContents: supabaseHomework.containers || [],
            content: supabaseHomework.content || '',
            containers: supabaseHomework.containers || [],
            attachments: supabaseHomework.attachments || [],
            bannerImage: supabaseHomework.bannerImage || null
          };

          console.log('Homework data for cache:', homeworkData);

          // Если hwId был timestamp, обновляем whiteboards с UUID
          const supabaseUUID = String(supabaseHomework.id);
          if (hwId !== supabaseUUID) {
            console.log('Updating whiteboard ID from timestamp to UUID:', hwId, '->', supabaseUUID);
            setWhiteboards(prev => prev.map(wb =>
              String(wb.id) === hwId && wb.type === 'homework'
                ? { ...wb, id: supabaseUUID }
                : wb
            ));

            // Обновляем homeworkContainers с новым ID
            setHomeworkContainers(prev => {
              const containers = prev[hwId] || [];
              const newContainers = { ...prev };
              delete newContainers[hwId];
              newContainers[supabaseUUID] = containers;
              return newContainers;
            });

            // Обновляем editorContents с новым ID
            setEditorContents(prev => {
              const newContents = { ...prev };
              Object.keys(prev).forEach(key => {
                if (key.startsWith(`homework-${hwId}-container-`)) {
                  const newKey = key.replace(`homework-${hwId}-container-`, `homework-${supabaseUUID}-container-`);
                  newContents[newKey] = prev[key];
                  delete newContents[key];
                }
              });
              return newContents;
            });
            // Обновляем ключи containerComments под новый homework UUID
            setContainerComments(prev => {
              const next = { ...prev };
              Object.keys(prev).forEach(key => {
                if (key.startsWith(`homework-${hwId}-container-`)) {
                  const newKey = key.replace(`homework-${hwId}-container-`, `homework-${supabaseUUID}-container-`);
                  next[newKey] = prev[key];
                  delete next[key];
                }
              });
              return next;
            });
          }

          // Сохраняем в кеш с UUID
          setHomeworkDataCache(prev => ({
            ...prev,
            [supabaseUUID]: homeworkData
          }));

          // Hydrate ExerciseSyncContext from the snapshot the student
          // attached at submit time. The context keys its localStorage entry
          // by lessonId + studentId/role, so we write the same blob under
          // the key this side of the call uses (teacher's case: the
          // student id from the URL; student's case: their own auth id).
          // The provider reads from localStorage on mount, so writing
          // before/while it mounts is enough — no API change needed.
          try {
            const savedExerciseStates = supabaseHomework?.content?.exerciseStates;
            if (savedExerciseStates && typeof savedExerciseStates === 'object') {
              const seedStudentKey = isStudent
                ? ((typeof currentUser?.id === 'string' && currentUser.id) || studentId || 'student')
                : (studentId || homeworkData.studentId || 'student');
              const lsKey = `ex_sync_${lessonId}_${seedStudentKey}`;
              const existingRaw = localStorage.getItem(lsKey);
              const existing = existingRaw ? (() => { try { return JSON.parse(existingRaw); } catch { return {}; } })() : {};
              // Merge — anything the local side already has wins for fields
              // it already populated, but missing exercises get filled in.
              const merged = { ...savedExerciseStates, ...existing };
              localStorage.setItem(lsKey, JSON.stringify(merged));
              // Also hand the fetched states to the provider directly — the
              // localStorage write above only helps on a subsequent page
              // reload, since the provider already mounted with the empty
              // snapshot from before this fetch resolved.
              setSubmittedExerciseStates(savedExerciseStates);
            }
          } catch (e) {
            console.warn('Could not hydrate exercise states from homework:', e?.message);
          }

          const containerContents = homeworkData.containerContents || homeworkData.containers || [];
          const finalHwId = hwId !== supabaseUUID ? supabaseUUID : hwId;

          if (containerContents.length > 0) {
            // Восстанавливаем контейнеры для этого homework
            setHomeworkContainers(prev => {
              // Обновляем контейнеры на основе сохраненных данных
              const newContainers = containerContents.map((cc) => ({
                id: cc.id,
                type: 'editor'
              }));

              return {
                ...prev,
                [finalHwId]: newContainers
              };
            });

            // Загружаем содержимое контейнеров в editorContents
            containerContents.forEach(containerContent => {
              const containerId = String(containerContent.id);
              const key = `homework-${finalHwId}-container-${containerId}`;
              setEditorContents(prev => {
                // If this key already holds non-empty content, don't clobber
                // it with the server's last-saved snapshot. This fires right
                // after the timestamp→UUID rename above (which renames this
                // same key, carrying over whatever the student has typed so
                // far under the temporary id) — unconditionally overwriting
                // here discarded that in-progress editing the instant the
                // UUID resolved, which is exactly why formatting/highlighting
                // a solo student had just added would vanish: the rename can
                // land mid-typing (it's a real network round trip), and
                // TextEditor's id-change effect then force-applies whatever
                // ends up here as the new initialContent.
                if (prev[key] && prev[key].trim() !== '') {
                  return prev;
                }
                const savedContent = containerContent.content || '';
                return {
                  ...prev,
                  [key]: savedContent
                };
              });
            });

            console.log('Homework containers loaded for:', currentHomeworkId, containerContents.length, 'isStudent:', isStudent, 'finalHwId:', finalHwId);
          } else {
            console.log('No container contents found for homework:', currentHomeworkId);
          }
        } catch (error) {
          console.error('Error loading homework containers:', error);
        }
      };

      loadHomeworkData();
    }
  // searchParams.toString() (a stable primitive), not searchParams itself —
  // useSearchParams() returns a new URLSearchParams object on every render,
  // so depending on the object made this effect (and its Supabase refetch +
  // editorContents reseed) re-run on literally every keystroke while typing
  // anywhere in the app, not just on actual homeworkId query-param changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeworkId, searchParams.toString(), isStudent, lessonId, studentId]);
}
